import type { v2, DeprecatedProperties, ControlTypeFunction } from '@sap/ux-specification-types';
import { ControlType, DefinitionName, QUICKVARPATH } from '@sap/ux-specification-types';
import {
    syncRule,
    validity,
    buildControlIdFromParent,
    convertSectionId,
    childId,
    descriptionSrcURL
} from '../../../common/decoration';
import {
    getAppSettingOBJsectionPathV2,
    getGlobalTableSettingsPathV2,
    getTableSettingsOBJsectionPathV2
} from '../../application';
import { addPatternForBindingChangeOfEnumOP } from '../../../common';
import { importCreateParameters, exportCreateParametersFields } from './Table';
import type { PopinLayout } from 'sap/m/library';
import type { smarttable } from 'sap/ui/comp/library';

const getSmartTableControlId = (
    baseId: string,
    _idBreadcrumbs: string[],
    breadcrumbs: string[],
    controlType: ControlTypeFunction,
    title: string
): string => {
    const breadCrumbsCopy = breadcrumbs.slice(1, 2);
    let suffixId: string;

    if (controlType() === 'sap.ui.comp.smarttable.SmartTable') {
        suffixId = DefinitionName.Table;
    } else if (controlType() === 'sap.m.Table') {
        suffixId = 'responsiveTable';
    }
    breadCrumbsCopy.push(suffixId);
    breadCrumbsCopy[0] = convertSectionId(breadCrumbsCopy[0], title);
    return buildControlIdFromParent(baseId, breadCrumbsCopy);
};

const syncRuleForSmartTableFlexProperty = {
    flex: {
        controlId: getSmartTableControlId,
        controlType: (): ControlType => ControlType.SmartTable
    }
};

const syncRuleForResponsiveTableFlexProperty = {
    flex: {
        controlId: getSmartTableControlId, // Note that this function evaluates the control type and returns the id of the inner table if applicable
        controlType: (): ControlType => ControlType.Table
    }
};

export class QuickVariantSelectionOP {
    @syncRule({
        manifest: {
            path: (pageKeys, manifest, sectionId): string =>
                getAppSettingOBJsectionPathV2(pageKeys, manifest, sectionId) + QUICKVARPATH
        }
    })
    @validity({
        since: '1.48.0'
    })
    showCounts?: boolean;

    @syncRule({
        manifest: {
            path: (pageKeys, manifest, sectionId): string =>
                getAppSettingOBJsectionPathV2(pageKeys, manifest, sectionId) + QUICKVARPATH
        }
    })
    @validity({
        since: '1.48.0'
    })
    variants: {
        [key: number]: v2.QuickVariant;
    };
}

export class Fields4DialogOP implements v2.Fields4DialogOP {
    @syncRule({
        manifest: {
            path: (pageKeys, manifest, sectionId): string =>
                getTableSettingsOBJsectionPathV2(pageKeys, manifest, sectionId) + '/createWithParameterDialog',
            export: exportCreateParametersFields
        }
    })
    @validity({
        since: '1.77.0'
    })
    fields: string[];
}

export class CommonTableSettingsForSections<
    COLS = v2.GenericColumns
> implements v2.CommonTableSettingsForSections<COLS> {
    getDeprecated = (): DeprecatedProperties => {
        return {
            tableType: {
                since: '1.50.0',
                deprecated: {
                    since: '1.70.0',
                    text: 'Use tableSettings.type instead'
                }
            }
        };
    };

    @syncRule({
        manifest: {
            key: 'createWithParameterDialog',
            path: getTableSettingsOBJsectionPathV2,
            import: importCreateParameters
        }
    })
    @validity({
        since: '1.77.0'
    })
    createWithParameterDialog?: Fields4DialogOP;

    @syncRule(syncRuleForSmartTableFlexProperty)
    @validity({
        since: '1.28.0'
    })
    editable?: boolean;

    @syncRule(syncRuleForSmartTableFlexProperty)
    @validity({
        since: '1.95.0' // git 5376552
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/f6a8fd2812d9442a9bba2f6fb296c42e')
    showPasteButton?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsOBJsectionPathV2
        }
    })
    @validity({
        since: '1.54.0'
    })
    multiSelect?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsOBJsectionPathV2
        }
    })
    @validity({
        since: '1.70.0'
    })
    selectAll?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsOBJsectionPathV2
        }
    })
    @validity({
        since: '1.70.0'
    })
    selectionLimit?: number;

    @syncRule({
        manifest: {
            path: getAppSettingOBJsectionPathV2
        }
    })
    @validity({
        since: '1.63.0'
    })
    quickVariantSelection?: QuickVariantSelectionOP;

    @syncRule(syncRuleForSmartTableFlexProperty)
    @validity({
        since: '1.145.0'
    })
    enableExport?: boolean;

    @syncRule(syncRuleForSmartTableFlexProperty)
    @validity({
        since: '1.26.0'
    })
    useExportToExcel?: boolean;

    @syncRule(syncRuleForSmartTableFlexProperty)
    @validity({
        since: '1.50.0'
    })
    exportType?: smarttable.ExportType;

    @syncRule({
        manifest: {
            path: getAppSettingOBJsectionPathV2
        }
    })
    @validity({
        since: '1.48.5'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/cfb04f0c58e7409992feb4c91aa9410b')
    createMode?: v2.CreateMode;

    @syncRule({
        manifest: {
            path: getAppSettingOBJsectionPathV2
        }
    })
    @validity({
        since: '1.61.0'
    })
    disableDefaultInlineCreateSort?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsOBJsectionPathV2
        }
    })
    @validity({
        since: '1.78.0'
    })
    variantManagement?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsOBJsectionPathV2
        }
    })
    @validity({
        since: '1.120.0'
    })
    copy?: boolean;

    @childId('toolBar')
    toolBar?: v2.ObjectPageToolBar;
}

export class ObjectPageResponsiveTable extends CommonTableSettingsForSections implements v2.ObjectPageResponsiveTable {
    @syncRule({
        manifest: {
            path: getTableSettingsOBJsectionPathV2
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/7f844f1021cd4791b8f7408eac7c1cec')
    type?: v2.TableTypeV2.ResponsiveTable;

    @syncRule(syncRuleForResponsiveTableFlexProperty)
    includeItemInSelection?: boolean;

    @syncRule({
        flex: syncRuleForResponsiveTableFlexProperty.flex,
        generate: addPatternForBindingChangeOfEnumOP
    })
    @validity({
        since: '1.52.0'
    })
    popinLayout?: PopinLayout;

    @syncRule({
        manifest: {
            path: getTableSettingsOBJsectionPathV2
        }
    })
    @validity({
        since: '1.77.0'
    })
    inlineDelete?: boolean;

    @syncRule(syncRuleForResponsiveTableFlexProperty)
    @validity({
        since: '1.16.0'
    })
    growingThreshold?: number;
}

export class ObjectPageAnalyticalTable extends CommonTableSettingsForSections implements v2.ObjectPageAnalyticalTable {
    @syncRule({
        manifest: {
            path: getTableSettingsOBJsectionPathV2
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/7f844f1021cd4791b8f7408eac7c1cec')
    type?: v2.TableTypeV2.AnalyticalTable;

    @syncRule({
        manifest: {
            path: getAppSettingOBJsectionPathV2
        }
    })
    @validity({
        since: '1.67.0'
    })
    condensedTableLayout?: boolean;
}

export class ObjectPageGridTable extends CommonTableSettingsForSections implements v2.ObjectPageGridTable {
    @syncRule({
        manifest: {
            path: getTableSettingsOBJsectionPathV2
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/7f844f1021cd4791b8f7408eac7c1cec')
    type?: v2.TableTypeV2.GridTable;

    @syncRule({
        manifest: {
            path: getAppSettingOBJsectionPathV2
        }
    })
    @validity({
        since: '1.67.0'
    })
    condensedTableLayout?: boolean;
}

export class ObjectPageTreeTable extends CommonTableSettingsForSections implements v2.ObjectPageTreeTable {
    @syncRule({
        manifest: {
            path: getTableSettingsOBJsectionPathV2
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/7f844f1021cd4791b8f7408eac7c1cec')
    type?: v2.TableTypeV2.TreeTable;

    @syncRule({
        manifest: {
            path: getAppSettingOBJsectionPathV2
        }
    })
    @validity({
        since: '1.67.0'
    })
    condensedTableLayout?: boolean;
}

export class GlobalTableSettings implements v2.GlobalTableSettings {
    @syncRule({
        manifest: {
            path: getGlobalTableSettingsPathV2
        }
    })
    @validity({
        since: '1.70.0'
    })
    selectAll?: boolean;

    @syncRule({
        manifest: {
            path: getGlobalTableSettingsPathV2
        }
    })
    @validity({
        since: '1.78.0'
    })
    variantManagement?: boolean;

    @syncRule({
        manifest: {
            path: getGlobalTableSettingsPathV2
        }
    })
    @validity({
        since: '1.120.0'
    })
    copy?: boolean;

    @syncRule({
        manifest: {
            path: getGlobalTableSettingsPathV2
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/7f844f1021cd4791b8f7408eac7c1cec')
    type?: v2.TableTypeV2;

    @syncRule({
        manifest: {
            path: getGlobalTableSettingsPathV2
        }
    })
    @validity({
        since: '1.54.0'
    })
    multiSelect?: boolean;

    @syncRule({
        manifest: {
            path: getGlobalTableSettingsPathV2
        }
    })
    @validity({
        since: '1.67.0'
    })
    condensedTableLayout?: boolean;

    @syncRule({
        manifest: {
            path: getGlobalTableSettingsPathV2
        }
    })
    @validity({
        since: '1.77.0'
    })
    inlineDelete?: boolean;
}
